import { lazy, Suspense } from "react";
import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route } from "react-router-dom";
import { AuthProvider } from "@/contexts/AuthContext";
import { ThemeProvider } from "next-themes";
import { DemoProvider } from "@/contexts/DemoContext";
import { BranchProvider } from "@/contexts/BranchContext";
import { ProtectedRoute } from "@/components/ProtectedRoute";
import { ErrorBoundary } from "@/components/ErrorBoundary";
import { AppLayout } from "@/components/layout/AppLayout";
import { Skeleton } from "@/components/ui/skeleton";

// Lazy-loaded pages
const AuthPage = lazy(() => import("./pages/AuthPage"));
const DemoEntryPage = lazy(() => import("./pages/DemoEntryPage"));
const AuthCallbackPage = lazy(() => import("./pages/AuthCallbackPage"));
const ResetPasswordPage = lazy(() => import("./pages/ResetPasswordPage"));
const SetupWizardPage = lazy(() => import("./pages/SetupWizardPage"));
const DashboardPage = lazy(() => import("./pages/DashboardPage"));
const BookingsPage = lazy(() => import("./pages/BookingsPage"));
const FleetPage = lazy(() => import("./pages/FleetPage"));
const VehicleDetailPage = lazy(() => import("./pages/VehicleDetailPage"));
const CustomersPage = lazy(() => import("./pages/CustomersPage"));
const FinancialsPage = lazy(() => import("./pages/FinancialsPage"));
const MaintenancePage = lazy(() => import("./pages/MaintenancePage"));
const MapPage = lazy(() => import("./pages/MapPage"));
const AlertsPage = lazy(() => import("./pages/AlertsPage"));
const SettingsPage = lazy(() => import("./pages/SettingsPage"));
const AvailabilityTablePage = lazy(() => import("./pages/AvailabilityTablePage"));
const PickupPage = lazy(() => import("./pages/PickupPage"));
const ReturnPage = lazy(() => import("./pages/ReturnPage"));
const AdminAuthPage = lazy(() => import("./pages/admin/AdminAuthPage"));
const AdminDashboardPage = lazy(() => import("./pages/admin/AdminDashboardPage"));
const InvitePage = lazy(() => import("./pages/InvitePage"));
const TermsPage = lazy(() => import("./pages/TermsPage"));
const PrivacyPage = lazy(() => import("./pages/PrivacyPage"));
const NotFound = lazy(() => import("./pages/NotFound"));

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      staleTime: 30_000,
      refetchOnWindowFocus: false,
    },
  },
});

const routerFuture = {
  v7_startTransition: true,
  v7_relativeSplatPath: true,
};

function PageLoader() {
  return (
    <div className="flex items-center justify-center min-h-[60vh]">
      <Skeleton className="h-8 w-48" />
    </div>
  );
}

const App = () => (
  <QueryClientProvider client={queryClient}>
    <ThemeProvider attribute="class" defaultTheme="system" enableSystem>
    <AuthProvider>
      <DemoProvider>
      <BranchProvider>
      <TooltipProvider>
        <ErrorBoundary>
          <Toaster />
          <Sonner />
          <BrowserRouter future={routerFuture}>
            <Suspense fallback={<PageLoader />}>
              <Routes>
                {/* Public routes */}
                <Route path="/" element={<AuthPage />} />
                <Route path="/demo" element={<DemoEntryPage />} />
                <Route path="/auth/callback" element={<AuthCallbackPage />} />
                <Route path="/reset-password" element={<ResetPasswordPage />} />
                <Route path="/invite" element={<InvitePage />} />
                <Route path="/terms" element={<TermsPage />} />
                <Route path="/privacy" element={<PrivacyPage />} />
                <Route path="/setup" element={<ProtectedRoute><SetupWizardPage /></ProtectedRoute>} />

                {/* Protected agency routes */}
                <Route element={<ProtectedRoute><AppLayout /></ProtectedRoute>}>
                  <Route path="/dashboard" element={<DashboardPage />} />
                  <Route path="/bookings" element={<BookingsPage />} />
                  <Route path="/fleet" element={<FleetPage />} />
                  <Route path="/fleet/:vehicleId" element={<VehicleDetailPage />} />
                  <Route path="/customers" element={<CustomersPage />} />
                  <Route path="/financials" element={<FinancialsPage />} />
                  <Route path="/maintenance" element={<MaintenancePage />} />
                  <Route path="/map" element={<MapPage />} />
                  <Route path="/alerts" element={<AlertsPage />} />
                  <Route path="/settings" element={<SettingsPage />} />
                  <Route path="/availability-table" element={<AvailabilityTablePage />} />
                </Route>

                {/* Protected standalone workflow pages */}
                <Route path="/bookings/:bookingId/pickup" element={<ProtectedRoute><PickupPage /></ProtectedRoute>} />
                <Route path="/bookings/:bookingId/return" element={<ProtectedRoute><ReturnPage /></ProtectedRoute>} />

                {/* Admin routes */}
                <Route path="/admin" element={<AdminAuthPage />} />
                <Route path="/admin/dashboard" element={<AdminDashboardPage />} />

                <Route path="*" element={<NotFound />} />
              </Routes>
            </Suspense>
          </BrowserRouter>
        </ErrorBoundary>
      </TooltipProvider>
      </BranchProvider>
      </DemoProvider>
    </AuthProvider>
    </ThemeProvider>
  </QueryClientProvider>
);

export default App;
